/**
 * RemotePage — mobile emergency controls.
 * Minimal UI: kill switch, agent start/stop, P&L summary.
 */

import { useState } from "react";
import {
  useAgentsOverview,
  useAgentLeaderboard,
  useKillSwitch,
  useStartAgent,
  useStopAgent,
} from "@/sdk/hooks/use-agents";
import { QueryErrorState } from "@/components/query-error-state";
import { TradingModeBadge } from "@/features/trading/components/TradingModeBadge";
import { KillSwitchStatus } from "@/features/trading/components/KillSwitchStatus";
import { asRecord } from "@/lib/type-utils";

export function RemotePage() {
  const { data: overview, isError: overviewError, refetch: refetchOverview } =
    useAgentsOverview(5_000);
  const { data: leaderboard, isError: leaderboardError } = useAgentLeaderboard(15_000);
  const killSwitch = useKillSwitch();
  const startAgent = useStartAgent();
  const stopAgent = useStopAgent();
  const [killConfirm, setKillConfirm] = useState(false);

  const agents = overview?.agents ? Object.values(overview.agents) : [];
  const running = agents.filter((a) => a.state === "running").length;
  const totalPnl =
    leaderboard?.leaderboard.reduce((s, a) => s + a.total_pnl, 0) ?? 0;
  const backendDown = overviewError || leaderboardError;

  const handleKill = () => {
    if (!killConfirm) {
      setKillConfirm(true);
      setTimeout(() => setKillConfirm(false), 4000);
      return;
    }
    killSwitch.mutate();
    setKillConfirm(false);
  };

  // A 2xx kill response is NOT automatically a clean full halt: the backend
  // reports trading_blocked / agent_registry_available / stopped[] and can
  // succeed on the breaker while the agent registry is unreachable. Render the
  // payload verdict, never just HTTP success.
  const killResult = asRecord(killSwitch.data);
  const killTradingBlocked = killResult.trading_blocked === true;
  const killRegistryAvailable = killResult.agent_registry_available === true;
  const killStoppedCount = Array.isArray(killResult.stopped)
    ? killResult.stopped.length
    : null;

  return (
    <div className="min-h-screen bg-zinc-950 p-4 text-white">
      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="text-lg font-bold">
          NeuralNet<span className="text-indigo-400">Ninja</span>{" "}
          <span className="text-xs text-zinc-500">Remote</span>
        </h1>
        <p className="mt-1 text-xs text-zinc-500">
          {backendDown ? "Status unavailable" : `${running}/${agents.length} agents running`}
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          <TradingModeBadge />
          <KillSwitchStatus />
        </div>
      </div>

      {backendDown ? (
        <QueryErrorState
          what="live bot status"
          onRetry={() => void refetchOverview()}
          className="mb-6"
        />
      ) : null}

      {/* Kill Switch */}
      <button
        onClick={handleKill}
        disabled={killSwitch.isPending}
        className={`mb-6 w-full rounded-xl py-6 text-center text-xl font-black uppercase tracking-widest transition-all ${
          killConfirm
            ? "animate-pulse bg-red-600 text-white"
            : "border-2 border-red-900 bg-red-950/50 text-red-400 active:bg-red-900"
        }`}
      >
        {killSwitch.isPending
          ? "HALTING..."
          : killConfirm
            ? "TAP AGAIN TO CONFIRM"
            : "EMERGENCY STOP"}
      </button>

      {killSwitch.isError ? (
        <p role="status" className="mb-6 -mt-3 text-center text-sm font-semibold text-red-400">
          Kill failed — tap to retry
        </p>
      ) : killSwitch.isSuccess ? (
        killTradingBlocked && killRegistryAvailable ? (
          <p role="status" className="mb-6 -mt-3 text-center text-sm font-semibold text-emerald-400">
            Emergency halt active — trading blocked
            {killStoppedCount != null
              ? `, ${killStoppedCount} agent${killStoppedCount === 1 ? "" : "s"} stopped`
              : ""}
          </p>
        ) : killTradingBlocked ? (
          <p role="status" className="mb-6 -mt-3 text-center text-sm font-semibold text-amber-400">
            Trading halted — agent registry unreachable, agents not stopped
          </p>
        ) : (
          <p role="status" className="mb-6 -mt-3 text-center text-sm font-semibold text-red-400">
            Halt unconfirmed — backend did not report trading blocked, check kill-switch status
          </p>
        )
      ) : null}

      {/* P&L Summary */}
      <div className="mb-6 rounded-lg border border-zinc-800 bg-zinc-900/50 p-4 text-center">
        <div className="text-xs text-zinc-500">Total P&L</div>
        <div
          className={`text-3xl font-bold font-mono ${
            backendDown ? "text-zinc-500" : totalPnl >= 0 ? "text-emerald-400" : "text-red-400"
          }`}
        >
          {backendDown ? "—" : `${totalPnl >= 0 ? "+" : ""}$${totalPnl.toFixed(2)}`}
        </div>
      </div>

      {/* Agent Controls */}
      <div className="space-y-2">
        {agents.map((agent) => {
          const isRunning = agent.state === "running";
          const startFailed =
            startAgent.isError && startAgent.variables === agent.agent_id;
          const stopFailed =
            stopAgent.isError && stopAgent.variables === agent.agent_id;
          return (
            <div
              key={agent.agent_id}
              className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-4 py-3"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 rounded-full ${isRunning ? "bg-emerald-500" : "bg-zinc-600"}`}
                    />
                    <span className="text-sm font-medium">{agent.name ?? agent.agent_name ?? agent.agent_id}</span>
                  </div>
                  <span
                    className={`text-xs font-mono ${(agent.realized_pnl ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}
                  >
                    ${(agent.realized_pnl ?? 0).toFixed(2)}
                  </span>
                </div>
                <button
                  onClick={() =>
                    isRunning
                      ? stopAgent.mutate(agent.agent_id)
                      : startAgent.mutate(agent.agent_id)
                  }
                  disabled={startAgent.isPending || stopAgent.isPending}
                  className={`rounded-lg px-4 py-2 text-xs font-bold uppercase ${
                    isRunning
                      ? "bg-red-950 text-red-400 active:bg-red-900"
                      : "bg-emerald-950 text-emerald-400 active:bg-emerald-900"
                  }`}
                >
                  {isRunning ? "Stop" : "Start"}
                </button>
              </div>
              {startFailed || stopFailed ? (
                <p role="status" className="mt-2 text-xs font-semibold text-red-400">
                  {stopFailed ? "Stop failed" : "Start failed"} — agent state unchanged, tap to retry
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Link to full terminal */}
      <div className="mt-8 text-center">
        <a
          href="/"
          className="text-xs text-indigo-400 underline"
        >
          Open full terminal
        </a>
      </div>
    </div>
  );
}
